import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Pencil } from "lucide-react";
import Select from "../../components/Inputs/Select";
import InlineSpinner from "../../components/shared/Spinner/InlineSpinner";
import AlertBanner from "../../components/shared/AlertBanner/AlertBanner";

import { createAgentFromDraft, generateAgentPrompt, listProviders, listAgents } from "../../api/providers";
import Loader from "../../components/Loader/Loader";
import UserAuthStore from "../../store/userStore";
import Header from "../../components/Header/Header";
import { CompactButton, PrimaryButton, SecondaryButton } from "../../components/Button/Buttons";
import FileUploadInput from "../../components/Inputs/FileUploadInput";
import Input from "../../components/Inputs/Input";
import TextArea from "../../components/Inputs/TextArea";
import ROUTES from "../../router/routes";
import { useQueryClient } from "@tanstack/react-query";
import FileEditorSection from "../../components/Forge/FileEditorSection";
import AgentSkillsSection from "../../components/Forge/AgentSkillsSection/AgentSkillsSection";
import AgentToolDiscoveryPanel from "../../components/Forge/AgentToolDiscoveryPanel";
import AgentAuthSection from "../../components/Forge/AgentAuthSection";
import AgentConnectionSection from "../../components/Forge/AgentConnectionSection";
import {
  createConnectionDrafts,
  getConnectionProtocol,
} from "../../components/Forge/connectionProtocols";
import DraftStatusBadge from "../../components/Forge/DraftStatusBadge";
import { useFormik } from "formik";
import * as Yup from "yup";
import logger from "../../services/Logger";
import Toggle from "../../components/shared/Toggle";
import {
  CONNECTION_FIELDS,
  pickConnection,
  readConnectionFromPolicy,
  writeConnectionToPolicy,
} from "../../utils/agentPolicyConnection";
import {
  createDefaultPolicyDraft,
  createDefaultToolsDraft,
  mergeDiscoveredToolDrafts,
  summarizeDraftChanges,
} from "../../utils/agentToolDrafts";
import useAgentToolDiscovery from "../../hooks/useAgentToolDiscovery";

const FIELD_LABEL_CLASS = "block text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300 mb-1.5";
const HELP_TEXT_CLASS = "text-[11px] text-ink-gray-400 mt-1";
const SECTION_HEADING_CLASS = "text-sm font-semibold text-ink-gray-900 dark:text-ink-white mb-4";

const AddAgentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const orgId = UserAuthStore((state) => state.orgId);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [connectionType, setConnectionType] = useState("mcp");
  const [connectionDrafts, setConnectionDrafts] = useState(createConnectionDrafts);
  const [error, setError] = useState(null);
  const [providers, setProviders] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [availableAgents, setAvailableAgents] = useState([]);
  
  // Get preselected provider from navigation state (from AddProviderPage)
  const preselectedProvider = location.state?.preselectedProvider;
  const providerName = location.state?.providerName;

  // Editor state for policy, tools, prompt
  const [editingFile, setEditingFile] = useState(null); // 'policy', 'tools', 'prompt'
  const [fileContent, setFileContent] = useState("");
  const [originalFileContent, setOriginalFileContent] = useState("");
  const [jsonError, setJsonError] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [draftContents, setDraftContents] = useState({
    policy: "",
    tools: "",
    prompt: "",
  });
  const [pendingFileBaselines, setPendingFileBaselines] = useState({});
  const [generationBaseDrafts, setGenerationBaseDrafts] = useState(null);
  const [generationRollback, setGenerationRollback] = useState(null);
  const [iconFile, setIconFile] = useState(null);
  const { discover: discoverTools, isDiscovering, progressLabel: discoveryProgressLabel } = useAgentToolDiscovery();
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);
  const [discoveredTools, setDiscoveredTools] = useState(null);
  const [selectedToolNames, setSelectedToolNames] = useState([]);
  const [discoveryError, setDiscoveryError] = useState(null);
  const [toolSearch, setToolSearch] = useState("");
  // Off by default: overwriting an existing prompt draft must be an explicit choice.
  const [regeneratePrompt, setRegeneratePrompt] = useState(false);
  const [generationResult, setGenerationResult] = useState(null);

  const filteredDiscoveredTools = useMemo(() => {
    if (!discoveredTools) return [];
    const query = toolSearch.trim().toLowerCase();
    if (!query) return discoveredTools;
    return discoveredTools.filter((tool) => (
      tool.name.toLowerCase().includes(query)
      || (tool.description || "").toLowerCase().includes(query)
    ));
  }, [discoveredTools, toolSearch]);

  const allFilteredSelected = filteredDiscoveredTools.length > 0
    && filteredDiscoveredTools.every((tool) => selectedToolNames.includes(tool.name));
  const someFilteredSelected = filteredDiscoveredTools.some((tool) => selectedToolNames.includes(tool.name));
  // Mirrors the prompt baseline handleGenerateToolDrafts will actually use.
  const promptDraftForGeneration = generationBaseDrafts
    ? generationBaseDrafts.prompt
    : (editingFile === "prompt" ? fileContent : draftContents.prompt);
  const currentFileHasGeneratedChanges = Boolean(
    editingFile
    && generationRollback
    && generationRollback.pendingFiles.includes(editingFile)
  );
  const editorIsDirty = Boolean(editingFile) && (
    fileContent !== originalFileContent || currentFileHasGeneratedChanges
  );
  const hasPendingEditorTransaction = editorIsDirty
    || Boolean(generationRollback)
    || Object.keys(pendingFileBaselines).length > 0;

  // In-memory copy of tools.json for skills management
  const [toolsContent, setToolsContent] = useState("");
  const isSwarmAgent = useMemo(() => {
    if (!toolsContent) return false;
    try { return "agents" in JSON.parse(toolsContent); } catch { return false; }
  }, [toolsContent]);

  const validationSchema = Yup.object().shape({
    agent_id: Yup.string()
      .trim()
      .matches(/^[a-z][a-z0-9_]*$/, "Must start with a letter — only lowercase letters, numbers, and underscores allowed (e.g. drive, agent_3d)")
      .required("Agent ID is required"),
    name: Yup.string()
      .trim()
      .required("Display Name is required"),
    provider: Yup.string()
      .required("Provider is required"),
    triggers: Yup.string()
      .trim()
      .required("At least one trigger is required"),
    policy_path: Yup.string()
      .required("Policy file is required"),
    tools_path: Yup.string()
      .required("Tools file is required"),
    prompt_path: Yup.string()
      .required("Prompt file is required"),
    mcp_connection: Yup.string()
      .trim()
      .url("Enter a valid URL (e.g. https://mcp.example.com/mcp)"),
  });

  const formik = useFormik({
    initialValues: {
      agent_id: "",
      name: "",
      triggers: "",
      description: "",
      service_type: "",
      provider: "",
      icon_path: "",
      policy_path: "",
      tools_path: "",
      prompt_path: "",
      enabled: true,
      mcp_connection: "",
      mcp_auth_token: "",
      auth_format: "raw",
      auth_header: "",
    },
    validationSchema,
    validateOnChange: false,
    onSubmit: handleSubmit,
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldError,
    setFieldTouched,
    submitCount,
  } = formik;

  // Fetch providers and agents
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        setLoadingProviders(true);
        const providersList = await listProviders(orgId);
        setProviders(providersList);
        
        // Fetch available agents for swarm agent selection
        const agentsList = await listAgents(orgId);
        setAvailableAgents(agentsList);
        
        // If a provider was preselected from AddProviderPage, auto-select it
        if (preselectedProvider && providersList.length > 0) {
          const selectedProvider = providersList.find(p => p.provider_id === preselectedProvider);
          if (selectedProvider) {
            setFieldValue("provider", selectedProvider.provider_id);
            setFieldValue("service_type", selectedProvider.service_type || "");
          }
        }
      } catch (err) {
        logger.error('AddAgentPage', 'Failed to fetch providers', { error: err.message });
        // Previously silent — an empty providers/agents list from a fetch
        // failure looked identical to the genuine "no providers yet" empty
        // state, with no way to tell the two apart.
        setError("Failed to load providers. Please refresh the page and try again.");
      } finally {
        setLoadingProviders(false);
      }
    };

    if (orgId) {
      fetchProviders();
    }
  }, [orgId, preselectedProvider, setFieldValue]);

  // policy.json stays the authoritative document: main-form connection edits are
  // written into whichever policy draft is live, and reads flow back the same way.
  const applyConnectionToPolicy = (connection, provider) => {
    setGenerationBaseDrafts((current) => current ? {
      ...current,
      policy: writeConnectionToPolicy(current.policy, connection, provider),
    } : current);
    setGenerationRollback((current) => current ? {
      ...current,
      drafts: {
        ...current.drafts,
        policy: writeConnectionToPolicy(current.drafts.policy, connection, provider),
      },
    } : current);
    if (editingFile === "policy") {
      setFileContent((current) => writeConnectionToPolicy(current, connection, provider));
      return;
    }
    setDraftContents((current) => ({
      ...current,
      policy: writeConnectionToPolicy(current.policy, connection, provider),
    }));
  };

  const syncConnectionFromPolicy = (policyJson) => {
    const connection = readConnectionFromPolicy(policyJson);
    if (!connection) return;
    CONNECTION_FIELDS.forEach((field) => setFieldValue(field, connection[field]));
  };

  const resetDiscovery = () => {
    setDiscoveredTools(null);
    setSelectedToolNames([]);
    setToolSearch("");
    setRegeneratePrompt(false);
    setGenerationResult(null);
  };

  const handleConnectionTypeChange = (nextType) => {
    setConnectionType(nextType);
    resetDiscovery();
    setDiscoveryError(null);
  };

  const handleProtocolDraftChange = (type, update) => {
    setConnectionDrafts((current) => ({
      ...current,
      [type]: typeof update === "function" ? update(current[type]) : update,
    }));
    resetDiscovery();
    setDiscoveryError(null);
  };

  // Patches several connection fields at once; applying them one at a time would
  // rebuild the policy from a stale `values` for every field after the first.
  const handleConnectionPatch = (patch) => {
    Object.entries(patch).forEach(([field, value]) => setFieldValue(field, value));
    applyConnectionToPolicy({ ...pickConnection(values), ...patch }, values.provider);
    resetDiscovery();
    setDiscoveryError(null);
  };

  const handleConnectionChange = (field, value) => handleConnectionPatch({ [field]: value });

  // Nothing is stored yet on this page, so only unreviewed generated output is flagged.
  const draftStatus = (fileType) => (
    generationRollback?.pendingFiles.includes(fileType) ? "generated" : null
  );

  // When provider is selected, auto-fill service_type
  const handleProviderSelect = (provider) => {
    const serviceType = provider.service_type || "";
    setFieldValue("provider", provider.provider_id);
    setFieldValue("service_type", serviceType);
    // `bearer` is only honoured for native providers.
    const connection = pickConnection(values);
    if (serviceType !== "native" && connection.auth_format === "bearer") {
      connection.auth_format = "raw";
      setFieldValue("auth_format", "raw");
    }
    applyConnectionToPolicy(connection, provider.provider_id);
    resetDiscovery();
    setDiscoveryError(null);
  };

  const handleDiscoverTools = async () => {
    setDiscoveryError(null);
    setGenerationResult(null);
    const protocol = getConnectionProtocol(connectionType);
    const discovery = protocol.discovery;
    const context = {
      provider: values.provider,
      connection: pickConnection(values),
      draft: connectionDrafts[connectionType] || null,
    };
    try {
      for (const field of discovery.validationFields) {
        setFieldTouched(field, true);
        await validationSchema.validateAt(field, values);
      }
      const tools = await discoverTools(discovery.buildRequest(context));
      setDiscoveredTools(tools);
      setSelectedToolNames([]);
      setToolSearch("");
    } catch (err) {
      if (err.name === "ValidationError" && err.path) setFieldError(err.path, err.message);
      setDiscoveryError(
        err.response?.data?.error || err.message || "Tool discovery failed"
      );
    }
  };

  // Applies only to the filtered view so an active search never drops selections made outside it.
  const handleToggleSelectAllFiltered = () => {
    const filteredNames = filteredDiscoveredTools.map((tool) => tool.name);
    setSelectedToolNames((current) => (
      allFilteredSelected
        ? current.filter((name) => !filteredNames.includes(name))
        : Array.from(new Set([...current, ...filteredNames]))
    ));
  };

  const handleGenerateToolDrafts = async () => {
    const selectedTools = discoveredTools.filter((tool) => selectedToolNames.includes(tool.name));
    const currentPolicy = editingFile === "policy"
      ? fileContent
      : draftContents.policy || createDefaultPolicyDraft(values);
    const currentTools = editingFile === "tools"
      ? fileContent
      : draftContents.tools || createDefaultToolsDraft();
    const currentPrompt = editingFile === "prompt" ? fileContent : draftContents.prompt;
    const baseDrafts = generationBaseDrafts || {
      policy: currentPolicy,
      tools: currentTools,
      prompt: currentPrompt,
    };
    const generated = mergeDiscoveredToolDrafts(
      baseDrafts.policy,
      baseDrafts.tools,
      selectedTools
    );
    setDiscoveryError(null);

    let prompt = baseDrafts.prompt;
    const promptWasEmpty = !prompt.trim();
    if (promptWasEmpty || regeneratePrompt) {
      if (!values.name.trim()) {
        setDiscoveryError("Enter a display name before generating the prompt.");
        return;
      }
      try {
        setIsGeneratingPrompt(true);
        const result = await generateAgentPrompt({
          agent_name: values.name,
          description: values.description,
          provider: values.provider,
          service_type: values.service_type,
          tools: selectedTools,
        });
        prompt = result.prompt;
      } catch (err) {
        setDiscoveryError(
          err.response?.data?.error || err.message || "Prompt generation failed"
        );
        return;
      } finally {
        setIsGeneratingPrompt(false);
      }
    }

    const generatedDrafts = { ...generated, prompt };
    const currentDrafts = { policy: currentPolicy, tools: currentTools, prompt: currentPrompt };
    const generatedFileTypes = ["policy", "tools", "prompt"]
      .filter((fileType) => currentDrafts[fileType] !== generatedDrafts[fileType]);
    setGenerationBaseDrafts(baseDrafts);
    if (generatedFileTypes.length) {
      setGenerationRollback((current) => {
        const rollback = current || {
          drafts: draftContents,
          paths: {
            policy: values.policy_path,
            tools: values.tools_path,
            prompt: values.prompt_path,
          },
        };
        const pendingFiles = ["policy", "tools", "prompt"]
          .filter((fileType) => generatedDrafts[fileType] !== rollback.drafts[fileType]);
        return pendingFiles.length ? { ...rollback, pendingFiles } : null;
      });
    }
    setDraftContents((current) => ({ ...current, ...generatedDrafts }));
    setToolsContent(generated.tools);
    ["policy", "tools", "prompt"].forEach((fileType) => {
      setFieldValue(`${fileType}_path`, `${fileType}-draft`);
    });
    setFieldError("policy_path", undefined);
    setFieldError("tools_path", undefined);
    setFieldError("prompt_path", undefined);
    if (editingFile) {
      setFileContent(generatedDrafts[editingFile]);
    }
    // Panel stays open with the selection intact: regenerating re-merges from
    // baseDrafts, so changing the selection and generating again is idempotent.
    setGenerationResult(summarizeDraftChanges(
      { policy: currentPolicy, tools: currentTools, prompt: currentPrompt },
      { ...generated, prompt },
      // eslint-disable-next-line no-nested-ternary
      promptWasEmpty ? "prompt generated" : regeneratePrompt ? "prompt regenerated" : "prompt preserved",
    ));
  };

  // Open a draft, preserving the current editor before switching files.
  const handleOpenFile = (fileType) => {
    setJsonError(null);
    setFileError(null);

    if (editingFile && editingFile !== fileType) {
      setDraftContents((current) => ({ ...current, [editingFile]: fileContent }));
      setFieldValue(`${editingFile}_path`, `${editingFile}-draft`);
      if (editingFile === "tools") setToolsContent(fileContent);
      if (editingFile === "policy") syncConnectionFromPolicy(fileContent);
      if (!currentFileHasGeneratedChanges && fileContent !== originalFileContent) {
        setPendingFileBaselines((current) => current[editingFile]
          ? current
          : { ...current, [editingFile]: originalFileContent });
      }
    }

    if (draftContents[fileType]) {
      setFileContent(draftContents[fileType]);
      setOriginalFileContent(pendingFileBaselines[fileType] ?? draftContents[fileType]);
      setEditingFile(fileType);
      return;
    }

    let template = "";
    if (fileType === "policy") {
      template = createDefaultPolicyDraft(values);
    } else if (fileType === "tools") {
      template = createDefaultToolsDraft();
    } else if (fileType === "prompt") {
      template = "You are an AI assistant that helps users with...";
    }
    setFileContent(template);
    setOriginalFileContent("");
    setJsonError(null);
    setEditingFile(fileType);
    // The template supplies defaults the main form does not (e.g. the token
    // placeholder), so pull them back up rather than leaving the fields blank.
    if (fileType === "policy") syncConnectionFromPolicy(template);
  };

  // Handle content change for policy and prompt
  const handleFileContentChange = (content) => {
    // Now handles both direct input (string from textarea) and DualViewEditor (string)
    const contentStr = typeof content === 'string' ? content : content.target?.value || '';
    setGenerationBaseDrafts(null);
    setGenerationResult(null);
    setFileContent(contentStr);
    
    if (editingFile === "policy" || editingFile === "tools") {
      try {
        JSON.parse(contentStr);
        setJsonError(null);
      } catch (err) {
        setJsonError(`Invalid JSON: ${err.message}`);
      }
    }
  };

  const handleSaveFile = () => {
    if ((editingFile === "policy" || editingFile === "tools") && jsonError) return;
    if (generationRollback?.pendingFiles.includes(editingFile)) {
      const pendingFiles = generationRollback.pendingFiles.filter((fileType) => fileType !== editingFile);
      if (pendingFiles.length) {
        setGenerationRollback({
          ...generationRollback,
          drafts: { ...generationRollback.drafts, [editingFile]: fileContent },
          paths: { ...generationRollback.paths, [editingFile]: `${editingFile}-draft` },
          pendingFiles,
        });
        setGenerationBaseDrafts((current) => current
          ? { ...current, [editingFile]: fileContent }
          : current);
      } else {
        setGenerationRollback(null);
        setGenerationBaseDrafts(null);
        setGenerationResult(null);
      }
    } else if (!generationRollback) {
      setGenerationBaseDrafts(null);
      setGenerationResult(null);
    }
    setDraftContents((current) => ({ ...current, [editingFile]: fileContent }));
    setPendingFileBaselines((current) => {
      const next = { ...current };
      delete next[editingFile];
      return next;
    });
    setFieldValue(`${editingFile}_path`, `${editingFile}-draft`);
    setFieldError(`${editingFile}_path`, undefined);
    if (editingFile === "tools") setToolsContent(fileContent);
    if (editingFile === "policy") syncConnectionFromPolicy(fileContent);
    setEditingFile(null);
    setFileContent("");
    setOriginalFileContent("");
  };

  const handleDraftFileSelected = async (fileType, file) => {
    setGenerationBaseDrafts(null);
    setGenerationResult(null);
    if (!file) {
      setDraftContents((current) => ({ ...current, [fileType]: "" }));
      setFieldValue(`${fileType}_path`, "", true);
      if (fileType === "tools") setToolsContent("");
      // An open editor's buffer wins at submit, so it must not outlive the draft.
      if (fileType === editingFile) {
        setFileContent("");
        setOriginalFileContent("");
      }
      return;
    }

    try {
      const content = await file.text();
      const formatted = fileType === "prompt"
        ? content
        : JSON.stringify(JSON.parse(content), null, 2);
      setDraftContents((current) => ({ ...current, [fileType]: formatted }));
      setFieldValue(`${fileType}_path`, `${fileType}-draft`);
      setFieldError(`${fileType}_path`, undefined);
      if (fileType === "tools") setToolsContent(formatted);
      if (fileType === "policy") syncConnectionFromPolicy(formatted);
      if (fileType === editingFile) {
        setFileContent(formatted);
        setOriginalFileContent(formatted);
      }
      setFileError(null);
    } catch (err) {
      setFileError(fileType === "prompt" ? "Failed to read prompt file" : `Invalid JSON in ${fileType}.json`);
      setFieldValue(`${fileType}_path`, "");
    }
  };

  // Cancel editing
  const handleCancelEditFile = () => {
    if (generationRollback && currentFileHasGeneratedChanges) {
      const restoredContent = generationRollback.drafts[editingFile];
      const pendingFiles = generationRollback.pendingFiles.filter((fileType) => fileType !== editingFile);
      setDraftContents((current) => ({ ...current, [editingFile]: restoredContent }));
      setFieldValue(`${editingFile}_path`, generationRollback.paths[editingFile]);
      if (editingFile === "tools") setToolsContent(restoredContent);
      if (editingFile === "policy") syncConnectionFromPolicy(restoredContent);
      if (pendingFiles.length) {
        setGenerationRollback({ ...generationRollback, pendingFiles });
      } else {
        setGenerationRollback(null);
        setGenerationBaseDrafts(null);
        setGenerationResult(null);
      }
    } else if (Object.prototype.hasOwnProperty.call(pendingFileBaselines, editingFile)) {
      const restoredContent = pendingFileBaselines[editingFile];
      setDraftContents((current) => ({ ...current, [editingFile]: restoredContent }));
      if (editingFile === "tools") setToolsContent(restoredContent);
      if (editingFile === "policy") syncConnectionFromPolicy(restoredContent);
      setPendingFileBaselines((current) => {
        const next = { ...current };
        delete next[editingFile];
        return next;
      });
    }
    setEditingFile(null);
    setFileContent("");
    setOriginalFileContent("");
    setJsonError(null);
    setFileError(null);
  };

  async function handleSubmit(formValues) {
    setIsSubmitting(true);
    setError(null);

    try {
      const connectionProtocol = getConnectionProtocol(connectionType);
      const connectionDraft = connectionDrafts[connectionType] || null;
      if (!connectionProtocol.isReady(connectionDraft)) {
        throw new Error(connectionProtocol.readinessError("creating"));
      }
      if (hasPendingEditorTransaction) {
        throw new Error(`Apply or discard the ${editingFile} changes before creating the agent`);
      }
      // Parse triggers from comma-separated string
      const triggersArray = formValues.triggers
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t.length > 0)
        .map((t) => (t.startsWith("@") ? t : `@${t}`));

      // effectiveDrafts reads the open editor's buffer, which bypasses the editor's
      // own Apply gate — so re-check validity here before it reaches the server.
      if (editingFile && jsonError) {
        throw new Error(`Fix the ${editingFile} validation errors before saving`);
      }
      const effectiveDrafts = editingFile
        ? { ...draftContents, [editingFile]: fileContent }
        : draftContents;
      if (!effectiveDrafts.policy || !effectiveDrafts.tools || !effectiveDrafts.prompt) {
        throw new Error("Policy, Tools, and Prompt drafts are required");
      }

      // The main form is the last word on connection details, so fold them in
      // even if the policy draft was created before they were entered.
      const policyContent = writeConnectionToPolicy(
        effectiveDrafts.policy,
        pickConnection(formValues),
        formValues.provider
      );

      const agentData = {
        agent_id: formValues.agent_id,
        name: formValues.name,
        triggers: triggersArray,
        provider: formValues.provider,
        description: formValues.description,
        service_type: formValues.service_type,
        enabled: formValues.enabled,
      };

      await createAgentFromDraft(agentData, {
        ...effectiveDrafts,
        policy: policyContent,
        icon: iconFile,
        connectionType,
        connectionSource: connectionProtocol.serializeSource(connectionDraft),
      });
      queryClient.invalidateQueries({ queryKey: ['get-agents', { org_id: orgId }] });
      queryClient.invalidateQueries({ queryKey: ['get-providers', orgId] });
      navigate(ROUTES.FORGE.ROOT);
    } catch (err) {
      setError(
        err.response?.data?.detail
        || err.response?.data?.error
        || err.message
        || "Failed to create agent"
      );
      setIsSubmitting(false);
    }
  }

  if (loadingProviders) {
    return (
      <div className="w-full">
        <Header type="forge" />
        <div className="h-[calc(100%-80px)] overflow-auto show-scrollbar">
          <div className="max-w-7xl mx-auto px-8 py-6">
            <Loader variant="form" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <Header type="forge" />
      <div className="h-[calc(100%-80px)] overflow-auto show-scrollbar">
        <div className="max-w-7xl mx-auto px-8 py-6">
          {/* Main Content Container */}
          <div className="bg-surface-panel dark:bg-surface-panel rounded-lg shadow-sm border border-edge-gray-200 dark:border-edge-gray-700 p-8">
            {/* Header section */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-lg font-semibold text-ink-ink dark:text-ink-white">
                  Add Agent
                </h1>
                <p className="text-sm text-ink-gray-500 dark:text-ink-gray-400 mt-1">
                  Create a new autonomous agent for your organization
                </p>
              </div>
              <div className="flex items-center gap-3">
                <SecondaryButton
                  type="button"
                  onClick={() => navigate(ROUTES.FORGE.ROOT)}
                  className="px-4 py-2"
                >
                  Cancel
                </SecondaryButton>
                <PrimaryButton
                  type="submit"
                  form="add-agent-form"
                  disabled={isSubmitting || hasPendingEditorTransaction
                    || !getConnectionProtocol(connectionType).isReady(
                      connectionDrafts[connectionType] || null
                    )}
                  className="px-6 py-2"
                >
                  {isSubmitting ? "Creating..." : "Create Agent"}
                </PrimaryButton>
              </div>
            </div>

            <hr className="border-edge-gray-200 dark:border-edge-gray-700 mb-8" />

            {/* Success message when coming from provider creation */}
            {preselectedProvider && providerName && (
              <AlertBanner variant="success" className="mb-6">
                <p className="font-medium">
                  Provider "{providerName}" created successfully!
                </p>
                <p className="text-xs mt-1">
                  Now add your first agent to complete the setup.
                </p>
              </AlertBanner>
            )}

            {error && <AlertBanner className="mb-6">{error}</AlertBanner>}

            <form id="add-agent-form" aria-label="Add agent" onSubmit={formik.handleSubmit}>
              <div className="space-y-8">
                {/* Section 1: Basic Information */}
                <div>
                  <h3 className={SECTION_HEADING_CLASS}>Basic Information</h3>
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div>
                        <label className={FIELD_LABEL_CLASS}>
                          Agent ID <span className="text-ink-red-500">*</span>
                        </label>
                        <Input
                          type="text"
                          name="agent_id"
                          value={values.agent_id}
                          onChange={(e) => {
                            // Strip spaces and convert to lowercase as user types
                            const sanitized = e.target.value.replace(/\s/g, '').toLowerCase();
                            setFieldValue('agent_id', sanitized);
                          }}
                          onBlur={handleBlur}
                          placeholder="e.g., drive, mail, hubspot"
                          error={Boolean(errors.agent_id && (touched.agent_id || submitCount > 0))}
                        />
                        <p className={HELP_TEXT_CLASS}>Must start with a letter — lowercase letters, numbers, underscores only (e.g. drive, agent_3d)</p>
                        {errors.agent_id && (touched.agent_id || submitCount > 0) && (
                          <p className="text-ink-red-500 text-xs mt-1">{errors.agent_id}</p>
                        )}
                      </div>
                      <div>
                        <label className={FIELD_LABEL_CLASS}>
                          Display Name <span className="text-ink-red-500">*</span>
                        </label>
                        <Input
                          type="text"
                          name="name"
                          value={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="e.g., Google Drive Agent"
                          error={Boolean(errors.name && (touched.name || submitCount > 0))}
                        />
                        {errors.name && (touched.name || submitCount > 0) && (
                          <p className="text-ink-red-500 text-xs mt-1">{errors.name}</p>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div>
                        <label className={FIELD_LABEL_CLASS}>
                          Provider <span className="text-ink-red-500">*</span>
                        </label>
                        {loadingProviders ? (
                          <div className="flex items-center gap-2 text-sm text-ink-gray-500 py-2.5">
                            <InlineSpinner />
                            Loading providers...
                          </div>
                        ) : (
                          <Select
                            value={values.provider}
                            onChange={(val) => handleProviderSelect(providers.find((p) => p.provider_id === val))}
                            placeholder="Select a provider"
                            error={!!(errors.provider && submitCount > 0)}
                            options={providers.map((p) => ({ value: p.provider_id, label: p.name, description: p.provider_id }))}
                          />
                        )}
                        {providers.length === 0 && !loadingProviders && (
                          <p className="text-xs text-ink-hex-d97706 mt-1">
                            No providers found. <button type="button" onClick={() => navigate(ROUTES.FORGE.ADD_PROVIDER)} className="underline">Add a provider first</button>
                          </p>
                        )}
                        {errors.provider && submitCount > 0 && (
                          <p className="text-ink-red-500 text-xs mt-1">{errors.provider}</p>
                        )}
                      </div>
                      <div>
                        <label className={FIELD_LABEL_CLASS}>
                          Service Type
                        </label>
                        <Input
                          type="text"
                          name="service_type"
                          value={values.service_type}
                          placeholder="Auto-filled from provider"
                          disabled
                        />
                        <p className={HELP_TEXT_CLASS}>Inherited from selected provider</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 2: Agent Details */}
                <div className="border-t border-edge-gray-200 dark:border-edge-gray-700 pt-6">
                  <h3 className={SECTION_HEADING_CLASS}>Agent Details</h3>
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div>
                        <label className={FIELD_LABEL_CLASS}>
                          Triggers <span className="text-ink-red-500">*</span>
                        </label>
                        <Input
                          type="text"
                          name="triggers"
                          value={values.triggers}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="@drive, @gdrive, @files"
                          error={Boolean(errors.triggers && (touched.triggers || submitCount > 0))}
                        />
                        <p className={HELP_TEXT_CLASS}>Comma-separated (@ prefix added automatically)</p>
                        {errors.triggers && (touched.triggers || submitCount > 0) && (
                          <p className="text-ink-red-500 text-xs mt-1">{errors.triggers}</p>
                        )}
                      </div>
                      <div>
                        <label className={FIELD_LABEL_CLASS}>
                          Icon
                        </label>
                        <FileUploadInput
                          orgId={orgId}
                          uploadType="agent_icon"
                          accept=".svg,.png,.jpg,.jpeg,.webp"
                          placeholder="Drop icon or click to browse"
                          deferUpload
                          onFileSelected={setIconFile}
                        />
                        <p className={HELP_TEXT_CLASS}>SVG, PNG, JPG or WebP</p>
                      </div>
                    </div>
                    <div>
                      <label className={FIELD_LABEL_CLASS}>
                        Description
                      </label>
                      <TextArea
                        name="description"
                        value={values.description}
                        onChange={handleChange}
                        placeholder="Brief description of what this agent does..."
                        rows={3}
                        className="resize-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Section 3: Connection */}
                <div className="border-t border-edge-gray-200 dark:border-edge-gray-700 pt-6">
                  <h3 className={SECTION_HEADING_CLASS}>Connection</h3>
                  <AgentConnectionSection
                    value={connectionType}
                    onChange={handleConnectionTypeChange}
                    connectionDrafts={connectionDrafts}
                    onConnectionDraftChange={handleProtocolDraftChange}
                  >
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div>
                        <label className={FIELD_LABEL_CLASS}>
                          MCP Connection
                        </label>
                        <Input
                          type="text"
                          name="mcp_connection"
                          value={values.mcp_connection}
                          onChange={(e) => handleConnectionChange("mcp_connection", e.target.value)}
                          onBlur={handleBlur}
                          placeholder="https://mcp.example.com/mcp"
                          error={Boolean(errors.mcp_connection && (touched.mcp_connection || submitCount > 0))}
                        />
                        <p className={HELP_TEXT_CLASS}>Leave blank for agents that only delegate to other agents</p>
                        {errors.mcp_connection && (touched.mcp_connection || submitCount > 0) && (
                          <p className="text-ink-red-500 text-xs mt-1">{errors.mcp_connection}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  </AgentConnectionSection>
                  <AgentToolDiscoveryPanel
                    canDiscover={getConnectionProtocol(connectionType).discovery.canRun({
                      provider: values.provider,
                      connection: pickConnection(values),
                      draft: connectionDrafts[connectionType] || null,
                    })}
                    isDiscovering={isDiscovering}
                    discoveryProgressLabel={discoveryProgressLabel}
                    onDiscover={handleDiscoverTools}
                    error={discoveryError}
                    tools={discoveredTools}
                    filteredTools={filteredDiscoveredTools}
                    selectedNames={selectedToolNames}
                    search={toolSearch}
                    onSearchChange={setToolSearch}
                    onToggleTool={(toolName, checked) => setSelectedToolNames((current) => (
                      checked ? [...current, toolName] : current.filter((name) => name !== toolName)
                    ))}
                    onToggleAll={handleToggleSelectAllFiltered}
                    allFilteredSelected={allFilteredSelected}
                    someFilteredSelected={someFilteredSelected}
                    onCancel={resetDiscovery}
                    onGenerate={handleGenerateToolDrafts}
                    isGenerating={isGeneratingPrompt}
                    canRegeneratePrompt={Boolean(promptDraftForGeneration.trim())}
                    regeneratePrompt={regeneratePrompt}
                    onRegeneratePromptChange={setRegeneratePrompt}
                    result={generationResult}
                  />
                  <div className="mt-6">
                    <AgentAuthSection values={values} onChange={handleConnectionPatch} />
                  </div>
                </div>

                {/* Section 4: Configuration Details */}
                <div className="border-t border-edge-gray-200 dark:border-edge-gray-700 pt-6">
                  <h3 className={SECTION_HEADING_CLASS}>
                    Configuration Details
                  </h3>
                  <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mb-4">
                    All three configuration files are required to create an agent.
                  </p>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div>
                      <label className={FIELD_LABEL_CLASS}>
                        Policy <span className="text-ink-red-500">*</span>
                        <DraftStatusBadge status={draftStatus("policy")} />
                      </label>
                      <div className="flex items-center gap-2">
                        <FileUploadInput
                          orgId={orgId}
                          uploadType="agent_policy"
                          accept=".json"
                          placeholder="Drop policy.json or click to browse"
                          deferUpload
                          disabled={hasPendingEditorTransaction}
                          onFileSelected={(file) => handleDraftFileSelected("policy", file)}
                          className="flex-1"
                        />
                        {editingFile !== "policy" && (
                          <CompactButton
                            type="button"
                            onClick={() => handleOpenFile("policy")}
                            disabled={Boolean(jsonError) || !values.agent_id || !values.provider}
                            className="h-[39px] whitespace-nowrap"
                            title={!values.agent_id || !values.provider ? "Enter Agent ID and select Provider first" : "Create policy JSON inline"}
                          >
                            <Pencil className="w-4 h-4" />
                            {values.policy_path ? "Edit" : "Create"}
                          </CompactButton>
                        )}
                      </div>
                      <p className={HELP_TEXT_CLASS}>Defines agent policies & rate limits</p>
                      {errors.policy_path && submitCount > 0 && (
                        <p className="text-ink-red-500 text-xs mt-1">{errors.policy_path}</p>
                      )}
                    </div>
                    <div>
                      <label className={FIELD_LABEL_CLASS}>
                        Tools <span className="text-ink-red-500">*</span>
                        <DraftStatusBadge status={draftStatus("tools")} />
                      </label>
                      <div className="flex items-center gap-2">
                        <FileUploadInput
                          orgId={orgId}
                          uploadType="agent_tools"
                          accept=".json"
                          placeholder="Drop tools.json or click to browse"
                          deferUpload
                          disabled={hasPendingEditorTransaction}
                          onFileSelected={(file) => handleDraftFileSelected("tools", file)}
                          className="flex-1"
                        />
                        {editingFile !== "tools" && (
                          <CompactButton
                            type="button"
                            onClick={() => handleOpenFile("tools")}
                            disabled={Boolean(jsonError) || !values.agent_id || !values.provider}
                            className="h-[39px] whitespace-nowrap"
                            title={!values.agent_id || !values.provider ? "Enter Agent ID and select Provider first" : "Create tools JSON inline"}
                          >
                            <Pencil className="w-4 h-4" />
                            {values.tools_path ? "Edit" : "Create"}
                          </CompactButton>
                        )}
                      </div>
                      <p className={HELP_TEXT_CLASS}>Define MCP tools & agents</p>
                      {errors.tools_path && submitCount > 0 && (
                        <p className="text-ink-red-500 text-xs mt-1">{errors.tools_path}</p>
                      )}
                    </div>
                    <div>
                      <label className={FIELD_LABEL_CLASS}>
                        Prompt <span className="text-ink-red-500">*</span>
                        <DraftStatusBadge status={draftStatus("prompt")} />
                      </label>
                      <div className="flex items-center gap-2">
                        <FileUploadInput
                          orgId={orgId}
                          uploadType="agent_prompt"
                          accept=".txt"
                          placeholder="Drop prompt.txt or click to browse"
                          deferUpload
                          disabled={hasPendingEditorTransaction}
                          onFileSelected={(file) => handleDraftFileSelected("prompt", file)}
                          className="flex-1"
                        />
                        {editingFile !== "prompt" && (
                          <CompactButton
                            type="button"
                            onClick={() => handleOpenFile("prompt")}
                            disabled={Boolean(jsonError) || !values.agent_id || !values.provider}
                            className="h-[39px] whitespace-nowrap"
                            title={!values.agent_id || !values.provider ? "Enter Agent ID and select Provider first" : "Create prompt text inline"}
                          >
                            <Pencil className="w-4 h-4" />
                            {values.prompt_path ? "Edit" : "Create"}
                          </CompactButton>
                        )}
                      </div>
                      <p className={HELP_TEXT_CLASS}>System prompt for the agent</p>
                      {errors.prompt_path && submitCount > 0 && (
                        <p className="text-ink-red-500 text-xs mt-1">{errors.prompt_path}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* File Editor (shown when creating/editing policy, tools, or prompt) */}
                {fileError && <p className="text-xs text-ink-red-500">{fileError}</p>}
                {editingFile && (
                  <div className="border-t border-edge-gray-200 dark:border-edge-gray-700 pt-6">
                    <FileEditorSection
                      fileType={editingFile}
                      fileContent={fileContent}
                      originalFileContent={originalFileContent}
                      isDirty={editorIsDirty}
                      onChange={handleFileContentChange}
                      onSave={handleSaveFile}
                      onCancel={handleCancelEditFile}
                      isSaving={false}
                      saveLabel="Apply"
                      cancelLabel="Discard"
                      jsonError={jsonError}
                      fileChangeDetection={{}}
                      availableAgents={availableAgents}
                      currentAgentId={values.agent_id}
                      onChangeDetected={() => {}}
                      connectionManagedExternally
                    />
                  </div>
                )}

                {/* Skills — expert agents only */}
                {!hasPendingEditorTransaction && !isSwarmAgent && values.tools_path && (
                  <AgentSkillsSection
                    toolsJsonContent={toolsContent}
                    onChange={(newContent) => {
                      setGenerationBaseDrafts(null);
                      setToolsContent(newContent);
                      setDraftContents((current) => ({ ...current, tools: newContent }));
                    }}
                    orgId={orgId}
                  />
                )}

                {/* Status */}
                <div className="border-t border-edge-gray-200 dark:border-edge-gray-700 pt-6">
                  <div className="flex items-center gap-3">
                    <Toggle
                      checked={values.enabled}
                      onChange={(nextValue) => setFieldValue("enabled", nextValue)}
                    />
                    <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
                      Enable agent
                    </label>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddAgentPage;
